import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import DatePickerRange from '@components/DatePickerRange/DatePickerRange';
import Button from '@components/Button/Button';
import { addDays } from 'date-fns';
import "react-datepicker/dist/react-datepicker.css";
import styles from './FilterConciliation.module.scss'

interface FilterConciliationProps {
    stagesList: Array<any>;
    institutionsList: Array<any>;
    drugsList: Array<any>;
    searchValues: any;
    handleGetData: () => void;
    handleSetSearchValues: (values: object) => void;
    handleSetAdministrationDateRange: (range: any) => void;
}

const FilterConciliation: React.FC<FilterConciliationProps> = ({
    stagesList ,
    searchValues ,
    institutionsList ,
    drugsList ,
    handleGetData ,
    handleSetSearchValues ,
    handleSetAdministrationDateRange
    }) => {

    const [unmodifiedFilters, setUnmodifiedFilters] = useState(true);
    const [seeFilters, setSeeFilters] = useState(false);
    const [inputTempValues, setInputTempValues] = useState({
        patientSearch: ''
    });

    const [ statesList , setStatesList] = useState<any>([])
    const [dateTypeFilter, setDateTypeFilter] = useState<'administracion' | 'elaboracion'>(
        searchValues.dateTypeFilter || 'administracion'
    );

    useEffect(() => {
        localStorage.setItem('searchValues', JSON.stringify(searchValues));
    }, [searchValues]);

    useEffect(() => {
        if (stagesList.length > 0) {
            const stage11 = stagesList.find((stage: any) => String(stage.id) === 'stage11');
            if (stage11) {
                setStatesList(stage11.availableStatuses);
            }
        }
    },[ stagesList ])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setInputTempValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleAreaChange = (selectedOption: any) => {
        const selectedArea = (selectedOption && selectedOption.value !== '')
            ? stagesList.find((stage: any) => String(stage.id) === String(selectedOption.value))
            : null;
        const updatedValues = { ...searchValues, selectedArea: selectedArea || '', stateSearch: [] };
        localStorage.setItem('searchValues', JSON.stringify(updatedValues));
        if (selectedArea) {
            setStatesList(selectedArea.availableStatuses);
        } else {
            setStatesList([]);
        }
        handleSetSearchValues((prevValues: any) => updatedValues);
        setUnmodifiedFilters(false);
    };

    const handleStateChange = (selectedOption: any) => {
        const selectedState = selectedOption?.value || '';
        if (selectedState === '') {
            handleSetSearchValues((prev: any) => {
                const updatedValues = {
                    ...prev,
                    stateSearch: [],
                };
                localStorage.setItem('searchValues', JSON.stringify(updatedValues));
                return updatedValues;
            });
        } else if (searchValues.stateSearch.filter((state: any) => state.name === selectedState).length === 0) {
            handleSetSearchValues((prev: any) => {
                const updatedValues = {
                    ...prev,
                    stateSearch: [...prev.stateSearch, statesList.find((state: any) => state.name === selectedState).name],
                };
                localStorage.setItem('searchValues', JSON.stringify(updatedValues));
                return updatedValues;
            });
        }
        // handleGetData();
        setUnmodifiedFilters(false);
    };

    const handleInstitutionChange = (selectedOption: any) => {
        const selectedInstitution = selectedOption ? institutionsList.find((inst: any) => inst.idOrg === selectedOption.value) : null;
        const updatedValues = { ...searchValues, selectedInstitution: selectedInstitution || '' };
        localStorage.setItem('searchValues', JSON.stringify(updatedValues));
        handleSetSearchValues((prevValues: any) => updatedValues);
        setUnmodifiedFilters(false);
    };

    const handleDrugChange = (selectedOption: any) => {
        const selectedDrug = selectedOption ? drugsList.find((inst: any) => Number(inst.id) === Number(selectedOption.value)) : null;
        const updatedValues = { ...searchValues, selectedDrug: selectedDrug || '' };
        localStorage.setItem('searchValues', JSON.stringify(updatedValues));
        handleSetSearchValues((prevValues: any) => updatedValues);
        setUnmodifiedFilters(false);
    };

    const handleClearFilters = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setDateTypeFilter('administracion');
        handleSetSearchValues({
            patientSearch: [],
            selectedInstitution: '' ,
            administrationStart: today, 
            administrationEnd: today,
            elaborationStart: today,
            elaborationEnd: today,
            dateTypeFilter: 'administracion',
            selectedArea: '' ,
            selectedDrug: '' ,
            stateSearch: []
        });
        setInputTempValues({
            patientSearch: ''
        })
        localStorage.setItem('searchValues' , JSON.stringify({
            patientSearch: [],
            selectedInstitution: '' ,
            administrationStart: today, 
            administrationEnd: today,
            elaborationStart: today,
            elaborationEnd: today,
            dateTypeFilter: 'administracion',
            selectedArea: '' ,
            selectedDrug: '' ,
            stateSearch: []
        }));
        setUnmodifiedFilters(true);
    };

    type SearchFields = 'patientSearch';
    const handleInputBlurOrEnter = (
        e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) => {
        const { name, value } = e.currentTarget as { name: SearchFields; value: string };

        if (e.type === 'keydown' && (e as React.KeyboardEvent).key !== 'Enter') {
            return;
        }

        if (name === 'patientSearch' && value.trim()) {
            if (!searchValues.patientSearch.some((item: string) => item.toLowerCase() === value.trim().toLowerCase())) {
                handleSetSearchValues((prevValues: any) => {
                    const updatedValues = {
                        ...prevValues,
                        patientSearch: [...prevValues.patientSearch, value.trim()],
                    };
                    localStorage.setItem('searchValues', JSON.stringify(updatedValues));
                    return updatedValues;
                });
                setInputTempValues((prevTempValues) => ({
                    ...prevTempValues,
                    [name]: '',
                }));
                setUnmodifiedFilters(false);
                handleGetData();
            }
        }
    };

    const removePatientSearchTerm = (term: string) => {
        handleSetSearchValues((prevValues: any) => {
            const updatedValues = {
                ...prevValues,
                patientSearch: prevValues.patientSearch.filter((value: string) => value !== term),
            };
            localStorage.setItem('searchValues', JSON.stringify(updatedValues));
            return updatedValues;
        });
        handleGetData();
    };

    const removeSelectedDrug = () => {
        handleSetSearchValues((prevValues: any) => {
            const updatedValues = {
                ...prevValues,
                selectedDrug: '',
            };
            localStorage.setItem('searchValues', JSON.stringify(updatedValues)); 
            return updatedValues;
        });
        handleGetData();
    };

    const removeSelectedInstitution = () => {
        handleSetSearchValues((prevValues: any) => {
            const updatedValues = {
                ...prevValues,
                selectedInstitution: '',
            };
            localStorage.setItem('searchValues', JSON.stringify(updatedValues)); 
            return updatedValues;
        });
        handleGetData();
    };

    const removeSelectedArea = () => {
        handleSetSearchValues((prevValues: any) => {
            const updatedValues = {
                ...prevValues,
                selectedArea: '',
            };
            localStorage.setItem('searchValues', JSON.stringify(updatedValues)); 
            return updatedValues;
        });
        handleGetData();
    };

    const removeStateSearchTerm = (term: string) => {
        handleSetSearchValues((prevValues: any) => {
            const updatedValues = {
                ...prevValues,
                stateSearch: prevValues.stateSearch.filter((value: string) => value !== term),
            };
            localStorage.setItem('searchValues', JSON.stringify(updatedValues));
            return updatedValues;
        });
    };

    const handleDateTypeChange = (type: 'administracion' | 'elaboracion') => {
        setDateTypeFilter(type);
        handleSetSearchValues((prev: any) => {
            const updated = { ...prev, dateTypeFilter: type };
            localStorage.setItem('searchValues', JSON.stringify(updated));
            return updated;
        });
        setUnmodifiedFilters(false);
    };

    const handleDateChange = (range: any) => {
        handleSetSearchValues((prev: any) => ({
            ...prev,
            administrationStart: range.startDate,
            administrationEnd: range.endDate,
            elaborationStart: range.startDate,
            elaborationEnd: range.endDate,
        }));
        setUnmodifiedFilters(false);
    };

    return <>
        <div className={styles.filterSection}>
                <div className={styles['filterSection-left']}>
                    
                </div>
                <div className={styles['filterSection-right']}>
                    <Button
                        label="Limpiar filtros"
                        type="primary"
                        icon="cleaner2"
                        iconPosition="left"
                        handleClick={handleClearFilters}
                        disabled={unmodifiedFilters}
                    />
                    <Button
                        label={ seeFilters ? 'Ocultar filtros' : 'Ver filtros' }
                        type="primary-outline"
                        icon={ seeFilters ? 'ojo4-close' : 'ojo4' }
                        iconPosition="left"
                        handleClick={() => setSeeFilters(!seeFilters)}
                    />
                </div>
            </div>

            {seeFilters && (
                <div className="cardGeneral mb-5">
                    <div className="row ">
                        <div className="col-12 col-md-4 col-xl-3 align-self-end">
                            <div className={styles.fieldBox}>
                                <label className={styles['fieldBox-label']}>Búsqueda por paciente</label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="iconwhite-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control text-uppercase"
                                        placeholder="Buscar"
                                        name="patientSearch"
                                        value={inputTempValues.patientSearch}
                                        onChange={handleInputChange}
                                        onBlur={handleInputBlurOrEnter}
                                        onKeyDown={handleInputBlurOrEnter}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-12 col-md-4 col-xl-3 align-self-end">
                            <div className={styles.fieldBox}>
                                <label className={styles['fieldBox-label']}>Preparado</label>
                                <Select
                                    options={drugsList.map((drug: any) => ({ value: drug.id, label: drug.name }))}
                                    value={searchValues.selectedDrug ? { value: searchValues.selectedDrug.id, label: searchValues.selectedDrug.name } : null}
                                    onChange={handleDrugChange}
                                    placeholder="Todas"
                                    isClearable
                                    isSearchable
                                    noOptionsMessage={() => 'No hay opciones disponibles'}
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '43px', width: '100%', borderColor: '#ced4da', '&:hover': { borderColor: '#ced4da' } }),
                                        menu: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                        </div>

                        <div className="col-12 col-md-4 col-xl-3 align-self-end">
                            <div className={styles.fieldBox}>
                                <label className={styles['fieldBox-label']}>Institución</label>
                                <Select
                                    options={institutionsList.map((inst: any) => ({ value: inst.idOrg, label: inst.name }))}
                                    value={searchValues.selectedInstitution ? { value: searchValues.selectedInstitution.idOrg, label: searchValues.selectedInstitution.name } : null}
                                    onChange={handleInstitutionChange}
                                    placeholder="Todas"
                                    isClearable
                                    isSearchable
                                    noOptionsMessage={() => 'No hay opciones disponibles'}
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '43px', width: '100%', borderColor: '#ced4da', '&:hover': { borderColor: '#ced4da' } }),
                                        menu: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                        </div>

                        <div className="col-12 col-md-4 col-xl-3 align-self-end">
                            <div className={styles.fieldBox}>
                                <label className={styles['fieldBox-label']}>Filtrar por fecha</label>
                                <div className={styles.formRadio}>
                                    <div className={styles['formRadio-radio']} onClick={() => handleDateTypeChange('administracion')}>
                                        <i className={`iconwhite-${dateTypeFilter === 'administracion' ? 'on' : 'off'}`}></i>
                                        Administración
                                    </div>
                                    <div className={styles['formRadio-radio']} onClick={() => handleDateTypeChange('elaboracion')}>
                                        <i className={`iconwhite-${dateTypeFilter === 'elaboracion' ? 'on' : 'off'}`}></i>
                                        Elaboración
                                    </div>
                                </div>
                                <DatePickerRange
                                    onChange={handleDateChange}
                                    initialStartDate={searchValues.administrationStart}
                                    initialEndDate={searchValues.administrationEnd}
                                />
                            </div>
                        </div>

                        <div className="col-12 col-md-4 col-xl-3 align-self-end">
                            <div className={styles.fieldBox}>
                                <label className={styles['fieldBox-label']}>Área de compounding</label>
                                <Select
                                    options={[{ value: '', label: 'Todas' }, ...stagesList.map((stage: any) => ({ value: stage.id, label: stage.name }))]}
                                    value={searchValues.selectedArea ? { value: searchValues.selectedArea.id, label: searchValues.selectedArea.name } : { value: '', label: 'Todas' }}
                                    onChange={handleAreaChange}
                                    placeholder="Todas"
                                    isClearable={false}
                                    isSearchable
                                    noOptionsMessage={() => 'No hay opciones disponibles'}
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '43px', width: '100%', borderColor: '#ced4da', '&:hover': { borderColor: '#ced4da' } }),
                                        menu: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                        </div>
                        

                        <div className="col-12 col-md-4 col-xl-3 align-self-end">
                            <div className={styles.fieldBox}>
                                <label className={styles['fieldBox-label']}>Estado</label>
                                <Select
                                    options={statesList.map((state: any) => ({ value: state.name, label: state.name }))}
                                    value={searchValues.stateSearch.length > 0 ? { value: searchValues.stateSearch[searchValues.stateSearch.length - 1], label: searchValues.stateSearch[searchValues.stateSearch.length - 1] } : null}
                                    onChange={handleStateChange}
                                    placeholder="Todos"
                                    isClearable
                                    isSearchable
                                    isDisabled={searchValues.selectedArea === ''}
                                    noOptionsMessage={() => 'No hay opciones disponibles'}
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '43px', width: '100%', borderColor: '#ced4da', '&:hover': { borderColor: '#ced4da' } }),
                                        menu: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles['filterSection-filters']}>
                        {searchValues.selectedDrug && (
                            <div className={styles['filterSection-filters-filter']}>
                                Droga: {searchValues.selectedDrug.name}
                                <div
                                    className={styles['filterSection-filters-filter-btnClose']}
                                    onClick={removeSelectedDrug}
                                >
                                    <i className="iconwhite-close"></i>
                                </div>
                            </div>
                        )}


                        {searchValues.patientSearch.map((item: string, index: number) => (
                                <div className={styles['filterSection-filters-filter']} key={index}>
                                    Paciente: {item}
                                    <div className={styles['filterSection-filters-filter-btnClose']} onClick={() => removePatientSearchTerm(item)}>
                                        <i className="iconwhite-close"></i>
                                    </div>
                                </div>
                            )
                        )}

                        {searchValues.selectedInstitution && (
                            <div className={styles['filterSection-filters-filter']}>
                                Institución: {searchValues.selectedInstitution.name}
                                <div
                                    className={styles['filterSection-filters-filter-btnClose']}
                                    onClick={removeSelectedInstitution}
                                >
                                    <i className="iconwhite-close"></i>
                                </div>
                            </div>
                        )}

                        {searchValues.selectedArea && (
                            <div className={styles['filterSection-filters-filter']}>
                                Área: {searchValues.selectedArea.name}
                                <div
                                    className={styles['filterSection-filters-filter-btnClose']}
                                    onClick={removeSelectedArea}
                                >
                                    <i className="iconwhite-close"></i>
                                </div>
                            </div>
                        )}
                        
                        {searchValues.stateSearch.map((state: any, index: number) => (
                            <div className={styles['filterSection-filters-filter']} key={`state-${index}`}>
                                Estado: {state}
                                <div
                                    className={styles['filterSection-filters-filter-btnClose']}
                                    onClick={() => removeStateSearchTerm(state)}
                                >
                                    <i className="iconwhite-close"></i>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
    </>
};

export default FilterConciliation;